import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  AppButton,
  PageHeader,
  StatusChip,
  toast,
} from "../../../../components/ui";
import { Box, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import {
  ClioService,
  HubspotService,
  RingCentralService,
  ZoomService,
} from "../../../../api";

type IntegrationTab = "clio" | "zoom" | "ringcentral" | "hubspot";

type ConnectionState = "checking" | "ok" | "error" | "unknown";

type StatusInfo = {
  state: ConnectionState;
  label: string;
  detail?: string;
};

function TabPanel({
  value,
  tab,
  children,
}: {
  value: IntegrationTab;
  tab: IntegrationTab;
  children: ReactNode;
}) {
  if (value !== tab) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

function statusTone(state: ConnectionState) {
  switch (state) {
    case "ok":
      return "dirtyGreen" as const;
    case "checking":
      return "secondary" as const;
    case "error":
      return "error" as const;
    default:
      return "neutral" as const;
  }
}

function IntegrationHeader({
  title,
  status,
  onRefresh,
  refreshing,
}: {
  title: string;
  status: StatusInfo;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      sx={{ mb: 2.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          {title}
        </Typography>
        {status.detail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {status.detail}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <StatusChip
          size="small"
          label={status.label}
          tone={statusTone(status.state)}
        />
        {onRefresh && (
          <AppButton
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={refreshing}
          >
            Refresh
          </AppButton>
        )}
      </Stack>
    </Stack>
  );
}

function OperationsList({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Stack spacing={1.5} divider={<Divider flexItem />}>
      {children}
    </Stack>
  );
}

function OperationRow({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>{action}</Box>
    </Stack>
  );
}

function parseClioWebhookStatus(raw: string | null | undefined): StatusInfo {
  const value = (raw ?? "").trim();
  if (!value || value === "Checking") {
    return {
      state: "checking",
      label: "Checking",
      detail: "Verifying Clio webhook automation…",
    };
  }
  if (value === "OK") {
    return {
      state: "ok",
      label: "Connected",
      detail: "Clio webhooks are responding.",
    };
  }
  return {
    state: "error",
    label: "Not working",
    detail: value || "Clio webhook automation is not healthy.",
  };
}

export default function IntegrationsSettings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<IntegrationTab>("clio");

  const [clioStatus, setClioStatus] = useState<StatusInfo>({
    state: "checking",
    label: "Checking",
  });
  const [zoomStatus, setZoomStatus] = useState<StatusInfo>({
    state: "checking",
    label: "Checking",
  });
  const [ringStatus, setRingStatus] = useState<StatusInfo>({
    state: "checking",
    label: "Checking",
  });
  const [hubspotStatus, setHubspotStatus] = useState<StatusInfo>({
    state: "unknown",
    label: "Not checked",
    detail: "Connect HubSpot or run a property sync to verify access.",
  });

  const [busy, setBusy] = useState<string | null>(null);

  const refreshClio = useCallback(async () => {
    setClioStatus({
      state: "checking",
      label: "Checking",
      detail: "Verifying Clio webhook automation…",
    });
    try {
      const status = await ClioService.getApiClioCheckWebhook();
      setClioStatus(parseClioWebhookStatus(status));
    } catch {
      setClioStatus({
        state: "error",
        label: "Unavailable",
        detail: "Could not reach Clio webhook status.",
      });
    }
  }, []);

  const refreshZoom = useCallback(async () => {
    setZoomStatus({
      state: "checking",
      label: "Checking",
      detail: "Loading Zoom users…",
    });
    try {
      const users = await ZoomService.getApiZoomUsers();
      const count = users?.length ?? 0;
      setZoomStatus({
        state: "ok",
        label: "Connected",
        detail: count > 0 ? `${count} Zoom user${count === 1 ? "" : "s"} available.` : "Zoom API responded.",
      });
    } catch {
      setZoomStatus({
        state: "error",
        label: "Unavailable",
        detail: "Could not load Zoom users. Check credentials and permissions.",
      });
    }
  }, []);

  const refreshRingCentral = useCallback(async () => {
    setRingStatus({
      state: "checking",
      label: "Checking",
      detail: "Loading RingCentral extensions…",
    });
    try {
      const extensions = await RingCentralService.getApiRingCentralExtensions();
      const count = extensions?.length ?? 0;
      setRingStatus({
        state: "ok",
        label: "Connected",
        detail:
          count > 0
            ? `${count} extension${count === 1 ? "" : "s"} available.`
            : "RingCentral API responded.",
      });
    } catch {
      setRingStatus({
        state: "error",
        label: "Unavailable",
        detail:
          "Could not load RingCentral extensions. Check credentials and permissions.",
      });
    }
  }, []);

  useEffect(() => {
    void refreshClio();
    void refreshZoom();
    void refreshRingCentral();
  }, [refreshClio, refreshZoom, refreshRingCentral]);

  const connectClio = async () => {
    setBusy("clio-connect");
    try {
      const url = await ClioService.getApiClioInitiateOauth();
      if (!url) {
        toast.error("Clio did not return an authorization URL.");
        return;
      }
      window.location.assign(url);
    } catch {
      toast.error("Could not start Clio authorization.");
    } finally {
      setBusy(null);
    }
  };

  const resetClioWebhooks = async () => {
    setBusy("clio-reset");
    try {
      await ClioService.postApiClioResetWebhooks();
      toast.success(
        "Automation reset. Wait a minute, then verify by changing a matter state or completing a task."
      );
      await refreshClio();
    } catch {
      toast.error("Could not reset Clio webhooks.");
    } finally {
      setBusy(null);
    }
  };

  const connectHubspot = async () => {
    setBusy("hubspot-connect");
    try {
      const url = await HubspotService.postApiHubspotInitiate();
      const href = typeof url === "string" ? url : url?.url ?? url?.toString?.();
      if (!href || typeof href !== "string") {
        toast.error("HubSpot did not return an authorization URL.");
        return;
      }
      window.location.assign(href);
    } catch {
      toast.error("Could not start HubSpot authorization.");
    } finally {
      setBusy(null);
    }
  };

  const updateHubspotWebhook = async () => {
    setBusy("hubspot-webhook");
    try {
      await HubspotService.postApiHubspotUpdateWebhook();
      setHubspotStatus({
        state: "ok",
        label: "Webhook updated",
        detail: "HubSpot webhook configuration was refreshed.",
      });
      toast.success("HubSpot webhook updated.");
    } catch {
      setHubspotStatus({
        state: "error",
        label: "Webhook failed",
        detail: "Could not update HubSpot webhook.",
      });
      toast.error("Could not update HubSpot webhook.");
    } finally {
      setBusy(null);
    }
  };

  const updateHubspotProperties = async () => {
    setBusy("hubspot-properties");
    try {
      await HubspotService.postApiHubspotUpdateHubspotProperties();
      setHubspotStatus({
        state: "ok",
        label: "Properties synced",
        detail: "HubSpot custom properties were updated.",
      });
      toast.success("HubSpot properties updated.");
    } catch {
      setHubspotStatus({
        state: "error",
        label: "Sync failed",
        detail: "Could not update HubSpot properties.",
      });
      toast.error("Could not update HubSpot properties.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 920, mx: "auto", pb: 4 }}>
      <PageHeader title="Integrations" />

      <Box
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, next: IntegrationTab) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="clio" label="Clio" />
            <Tab value="zoom" label="Zoom" />
            <Tab value="ringcentral" label="RingCentral" />
            <Tab value="hubspot" label="HubSpot" />
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, md: 2.75 }, pb: { xs: 2, md: 2.75 } }}>
          <TabPanel value={tab} tab="clio">
            <IntegrationHeader
              title="Clio"
              status={clioStatus}
              onRefresh={refreshClio}
              refreshing={clioStatus.state === "checking"}
            />
            <OperationsList>
              <OperationRow
                title="Connect / reconnect"
                description="Start Clio OAuth to authorize this application."
                action={
                  <AppButton
                    variant="contained"
                    color="primary"
                    startIcon={<LinkIcon />}
                    onClick={connectClio}
                    disabled={busy === "clio-connect"}
                  >
                    {busy === "clio-connect" ? "Starting…" : "Connect Clio"}
                  </AppButton>
                }
              />
              <OperationRow
                title="Reset webhooks"
                description="Rebuild Clio automation webhooks for matters and tasks."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    startIcon={<RestartAltIcon />}
                    onClick={resetClioWebhooks}
                    disabled={busy === "clio-reset"}
                  >
                    {busy === "clio-reset" ? "Resetting…" : "Reset webhooks"}
                  </AppButton>
                }
              />
              <OperationRow
                title="Matter tools"
                description="Open Clio matter interaction tools."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/matterInteractions")}
                  >
                    Open
                  </AppButton>
                }
              />
              <OperationRow
                title="Task Templates"
                description="Edit Clio task templates and manage task types."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/taskInteractions")}
                  >
                    Open
                  </AppButton>
                }
              />
              <OperationRow
                title="Matter folders"
                description="Inspect and ensure Clio matter folder structure."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/matterFolderStructure")}
                  >
                    Open
                  </AppButton>
                }
              />
            </OperationsList>
          </TabPanel>

          <TabPanel value={tab} tab="zoom">
            <IntegrationHeader
              title="Zoom"
              status={zoomStatus}
              onRefresh={refreshZoom}
              refreshing={zoomStatus.state === "checking"}
            />
            <OperationsList>
              <OperationRow
                title="Phone communications"
                description="Review and attach Zoom phone communications to matters."
                action={
                  <AppButton
                    variant="contained"
                    color="primary"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/zoom")}
                  >
                    Open Zoom
                  </AppButton>
                }
              />
              <OperationRow
                title="Recordings"
                description="Search and manage Zoom call recordings."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/zoom/recordings")}
                  >
                    Open recordings
                  </AppButton>
                }
              />
            </OperationsList>
          </TabPanel>

          <TabPanel value={tab} tab="ringcentral">
            <IntegrationHeader
              title="RingCentral"
              status={ringStatus}
              onRefresh={refreshRingCentral}
              refreshing={ringStatus.state === "checking"}
            />
            <OperationsList>
              <OperationRow
                title="Phone communications"
                description="Review and attach RingCentral communications to matters."
                action={
                  <AppButton
                    variant="contained"
                    color="primary"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/ringcentral")}
                  >
                    Open RingCentral
                  </AppButton>
                }
              />
              <OperationRow
                title="Recordings"
                description="Search and manage RingCentral recordings."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate("/ringcentral/recordings")}
                  >
                    Open recordings
                  </AppButton>
                }
              />
            </OperationsList>
          </TabPanel>

          <TabPanel value={tab} tab="hubspot">
            <IntegrationHeader title="HubSpot" status={hubspotStatus} />
            <OperationsList>
              <OperationRow
                title="Connect / reconnect"
                description="Start HubSpot OAuth to authorize this application."
                action={
                  <AppButton
                    variant="contained"
                    color="primary"
                    startIcon={<LinkIcon />}
                    onClick={connectHubspot}
                    disabled={busy === "hubspot-connect"}
                  >
                    {busy === "hubspot-connect"
                      ? "Starting…"
                      : "Connect HubSpot"}
                  </AppButton>
                }
              />
              <OperationRow
                title="Update webhook"
                description="Refresh HubSpot webhook subscription settings."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    startIcon={<RestartAltIcon />}
                    onClick={updateHubspotWebhook}
                    disabled={busy === "hubspot-webhook"}
                  >
                    {busy === "hubspot-webhook" ? "Updating…" : "Update webhook"}
                  </AppButton>
                }
              />
              <OperationRow
                title="Sync properties"
                description="Push HubSpot custom property definitions used by intake."
                action={
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshIcon />}
                    onClick={updateHubspotProperties}
                    disabled={busy === "hubspot-properties"}
                  >
                    {busy === "hubspot-properties"
                      ? "Syncing…"
                      : "Sync properties"}
                  </AppButton>
                }
              />
            </OperationsList>
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
}
